import { useMemo, useState } from "react";
import {
  ChevronRight,
  RotateCcw,
  Search,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { EmployeeAvatar } from "../components/EmployeeAvatar";
import { SortableTableHead } from "../components/SortableTableHead";
import { StatusBadge } from "../components/StatusBadge";
import { TablePagination } from "../components/TablePagination";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { companyOptions, statusOptions } from "../data";
import { useAdminBeneficiaries } from "../hooks/useAdminBeneficiaries";
import { ExportMenu } from "../components/ExportMenu";
import { exportCsv } from "@/shared/lib/exportCsv";

export function BeneficiariesPage() {
  const { t } = useTranslation();
  const paginationLabels = t.safecheck.tablePagination;
  const { beneficiaries, total, loading, error, usingApi } = useAdminBeneficiaries();
  const [topSearch, setTopSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [birthDateFilter, setBirthDateFilter] = useState("");
  const [ageMinFilter, setAgeMinFilter] = useState("");
  const [ageMaxFilter, setAgeMaxFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("name", "asc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const filtered = useMemo(() => {
    return beneficiaries.filter((beneficiary) => {
      const byTopSearch = topSearch
        ? `${beneficiary.name} ${beneficiary.matricule} ${beneficiary.employee}`
            .toLowerCase()
            .includes(topSearch.toLowerCase())
        : true;
      const byCompany = companyFilter
        ? beneficiary.company.toLowerCase().includes(companyFilter.toLowerCase())
        : true;
      const byRelation = relationFilter
        ? beneficiary.relation.toLowerCase().includes(relationFilter.toLowerCase())
        : true;
      const byGender = genderFilter ? beneficiary.gender === genderFilter : true;
      const byBirthDate = birthDateFilter ? beneficiary.birthDate === birthDateFilter : true;
      const byAgeMin = ageMinFilter ? Number.parseInt(beneficiary.age, 10) >= Number(ageMinFilter) : true;
      const byAgeMax = ageMaxFilter ? Number.parseInt(beneficiary.age, 10) <= Number(ageMaxFilter) : true;
      const byStatus = statusFilter ? beneficiary.status === statusFilter : true;
      const byTableSearch = tableSearch
        ? `${beneficiary.name} ${beneficiary.employee} ${beneficiary.matricule}`
            .toLowerCase()
            .includes(tableSearch.toLowerCase())
        : true;

      return (
        byTopSearch &&
        byCompany &&
        byRelation &&
        byGender &&
        byBirthDate &&
        byAgeMin &&
        byAgeMax &&
        byStatus &&
        byTableSearch
      );
    });
  }, [
    beneficiaries,
    topSearch,
    companyFilter,
    relationFilter,
    genderFilter,
    birthDateFilter,
    ageMinFilter,
    ageMaxFilter,
    statusFilter,
    tableSearch,
  ]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (beneficiary, key) => {
        if (key === "name") return beneficiary.name.replace(/^\d+\.\s*/, "");
        if (key === "relation") return beneficiary.relation;
        if (key === "birthDate") return beneficiary.birthDate;
        if (key === "gender") return beneficiary.gender;
        if (key === "employee") return beneficiary.employee;
        if (key === "matricule") return beneficiary.matricule;
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const columnDefs = [
    { key: "photo", label: t.safecheck.beneficiaries.columns.photo, sortable: false },
    { key: "name", label: t.safecheck.beneficiaries.columns.name, sortable: true },
    { key: "relation", label: t.safecheck.beneficiaries.columns.relation, sortable: true },
    { key: "birthDate", label: t.safecheck.beneficiaries.columns.birthDate, sortable: true },
    { key: "gender", label: t.safecheck.beneficiaries.columns.gender, sortable: true },
    { key: "employee", label: t.safecheck.beneficiaries.columns.employee, sortable: true },
    { key: "matricule", label: t.safecheck.beneficiaries.columns.matricule, sortable: true },
  ] as const;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.employees, href: "/dashboard/employees" },
          { label: t.safecheck.beneficiaries.title },
        ]}
      />

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">{t.safecheck.beneficiaries.title}</h1>
      </div>

      {error && (
        <p className="mb-3 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {loading && <p className="mb-3 text-sm text-muted">Chargement des bénéficiaires…</p>}
      {usingApi && !loading && !error && (
        <p className="mb-3 text-xs text-muted-foreground">{total} bénéficiaire(s) — base de données</p>
      )}

      <div className="theme-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Rechercher..."
            className={`${inputClass} w-full max-w-[260px]`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setTopSearch("");
                setCompanyFilter("");
                setRelationFilter("");
                setGenderFilter("");
                setBirthDateFilter("");
                setAgeMinFilter("");
                setAgeMaxFilter("");
                setStatusFilter("");
                setTableSearch("");
                setPage(1);
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent"
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="header-ribbon filter-panel relative isolate rounded-2xl p-4">
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Search size={14} />
            <span>{t.safecheck.filters}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            <FormField label={t.safecheck.fields.company}>
              <select
                value={companyFilter}
                onChange={(event) => {
                  setCompanyFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {companyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={t.safecheck.beneficiaries.fields.relation}>
              <select
                value={relationFilter}
                onChange={(event) => {
                  setRelationFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                <option value="">{t.safecheck.beneficiaries.fields.allRelations}</option>
                <option value="enfant">Enfant</option>
                <option value="conjoint">Conjoint</option>
              </select>
            </FormField>
            <FormField label={t.safecheck.beneficiaries.fields.gender}>
              <select
                value={genderFilter}
                onChange={(event) => {
                  setGenderFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                <option value="">{t.safecheck.beneficiaries.fields.allGenders}</option>
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
            </FormField>
            <FormField label={t.safecheck.beneficiaries.fields.birthDate}>
              <input
                value={birthDateFilter}
                onChange={(event) => {
                  setBirthDateFilter(event.target.value);
                  setPage(1);
                }}
                type="date"
                className={inputClass}
              />
            </FormField>
            <FormField label={t.safecheck.beneficiaries.fields.ageMin}>
              <input
                value={ageMinFilter}
                onChange={(event) => {
                  setAgeMinFilter(event.target.value);
                  setPage(1);
                }}
                className={inputClass}
                placeholder="Ex: 25"
                type="number"
              />
            </FormField>
            <FormField label={t.safecheck.beneficiaries.fields.ageMax}>
              <input
                value={ageMaxFilter}
                onChange={(event) => {
                  setAgeMaxFilter(event.target.value);
                  setPage(1);
                }}
                className={inputClass}
                placeholder="Ex: 65"
                type="number"
              />
            </FormField>
            <FormField label={t.safecheck.fields.status}>
              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
            >
              <Search size={15} />
              {t.safecheck.filter}
            </button>
            <button
              type="button"
              onClick={() => {
                setCompanyFilter("");
                setRelationFilter("");
                setGenderFilter("");
                setBirthDateFilter("");
                setAgeMinFilter("");
                setAgeMaxFilter("");
                setStatusFilter("");
                setPage(1);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent"
            >
              <RotateCcw size={15} />
              {t.safecheck.reset}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-border py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
            >
              {[10, 25, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ExportMenu
              onExport={() =>
                exportCsv(
                  "beneficiaires.csv",
                  columnDefs.filter((c) => c.key !== "photo" && c.key !== "actions").map((c) => c.label),
                  sorted.map((b) => [b.name, b.relation, b.birthDate, b.gender, b.employee, b.matricule]),
                )
              }
            />
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/40">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((beneficiary) => (
                <tr key={beneficiary.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ChevronRight size={14} className="text-muted" />
                      <EmployeeAvatar name={beneficiary.name} photo={beneficiary.photo} size={36} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground">{beneficiary.name}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{beneficiary.relation}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{beneficiary.birthDate || "—"}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{beneficiary.gender}</td>
                  <td className="px-4 py-3 text-sm text-primary">{beneficiary.employee}</td>
                  <td className="px-4 py-3">
                    <StatusBadge variant="purple">{beneficiary.matricule}</StatusBadge>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted">
                    Aucun bénéficiaire trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={handlePageChange}
          labels={paginationLabels}
        />
      </div>
    </>
  );
}
